/*
 *  (c) 2015 - 2024 ENISCO by FORCAM GmbH
 */

import React, {useCallback, useEffect, useState} from 'react'
import TabPanel from '../TabPanel'
import useFavoriteGroups from '../../../../hooks/useFavoriteGroups'
import {Favorite, FavoriteApp, Group} from '../../../../model/favorites'
import {clone, translateText, useError, useLanguage, useNotification, useResolver} from '@enisco/mcc-react-lib'
import useLoadProfile from '../../../../hooks/useLoadProfile'
import {useIntl} from 'react-intl'
import FavoriteViewerGroup from './FavoriteViewerGroup'

interface Props {
  handleClose: () => void
  value: number
  index: number
}

const FavoritesTab: React.FC<Props> = ({handleClose, value, index}) => {
  const [favoriteGroups] = useFavoriteGroups()
  const [groups, setGroups] = useState<Group[]>([])
  const language = useLanguage()
  const error = useError()
  const resolver = useResolver()
  const [loadProfile] = useLoadProfile()
  const notification = useNotification()
  const intl = useIntl()

  useEffect(() => {
    const sortedGroups = clone(favoriteGroups || [])
    sortedGroups.sort((a, b) =>
      (translateText(language, a.description) ?? '').localeCompare(
        translateText(language, b.description) ?? ''
      )
    )
    setGroups(sortedGroups)
  }, [favoriteGroups, language])

  const updateFavorite = useCallback(
    (favorite: Favorite) => {
      if (favorite) {
        resolver('mcc-portal-api', '/internal/favorites/' + favorite.id, {
          options: {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(favorite)
          }
        })
          .then(() => {
            return loadProfile()
          })
          .catch(error)
      }
    },
    [error, loadProfile, resolver]
  )

  const moveFavorite = useCallback(
    (favoriteApp: FavoriteApp, group: Group) => {
      const favorite = favoriteApp.favorite
      if (group.id && favorite.groupId !== group.id) {
        if (group.user && !favorite.user) {
          notification(
            'info',
            intl.formatMessage({
              id: 'FavoriteOptions.EditFavoriteInfo',
              defaultMessage:
                "Global favorite can't be moved into a personal group"
            })
          )
          return
        }
        const favoriteToUpdate = clone(favorite)
        favoriteToUpdate.groupId = group.id
        favoriteToUpdate.deleteGroupIfEmpty = true
        updateFavorite(favoriteToUpdate)
      }
    },
    [intl, notification, updateFavorite]
  )

  return (
    <TabPanel value={value} index={index} data-testid={'FavoritesTab:root'}>
      {groups.map((group: Group) => (
        <FavoriteViewerGroup
          handleClose={handleClose}
          group={group}
          key={group.id}
          moveFavorite={moveFavorite}
        />
      ))}
    </TabPanel>
  )
}

export default FavoritesTab
